import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import ToothCorrection, { gumVertex, toothVertex } from '@correction/three/ToothCorrection.ts';
import {
  BufferAttribute,
  BufferGeometry,
  Group,
  Intersection,
  Matrix4,
  Mesh,
  Object3D,
  Quaternion,
  Vector3,
} from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import MeshUtils from '@modules/three/common/meshUtils';
import {
  fillMinorBoundaryLoops,
  markInterproximalRegion,
  markSharpFaces,
  mergePositionIndexGeometries,
  projectOntoSource,
  removeSmallComponents,
  smoothSurface,
  subdivideLongTriangles,
  weldMeshVertices,
} from '@modules/bracesDesigner/export/aligner/alignerMeshShell';
import { fuseSeamTJunctions, repairToManifold } from '@modules/bracesDesigner/export/aligner/seamFusion';
import {
  rebuildInterproximalSpace,
  stripGeneratedFaces,
} from '@modules/bracesDesigner/export/aligner/interproximalRebuild';
import { extrudeGumSkirt } from '@modules/bracesDesigner/export/aligner/gumSkirt';
import { buildVoxelSkirtBody } from '@modules/bracesDesigner/export/aligner/voxelSkirt';
import {
  buildOffsetShell,
  countOpenEdges,
  type OffsetShellOptions,
} from '@modules/bracesDesigner/export/aligner/offsetShell';
import { buildInsideDistanceField } from '@modules/bracesDesigner/export/aligner/levelSetOffset';
import { buildVoxelInteriorShell } from '@modules/bracesDesigner/export/aligner/voxelInterior';
import {
  SDFGenerator,
  applySheetSlab,
  blockoutInterproximalWebs,
  closeSDFInPlace,
  extractMesh,
  getSdfWorkerPool,
} from '@modules/three/common/voxels';
import { geometryToPly } from '@modules/three/utils/meshExporter';
import RaycastEvents from '@modules/three/common/raycastEvents.ts';
import JawsCorrection from '@correction/three/JawsCorrection.ts';
import { gumMaterial } from '@modules/three/common/materials/jawMaterials';
import TreatmentCyclesTransformation from '@correction/three/TreatmentCyclesTransformation.ts';
import { JawExportDTO, TeethExportDTO, ToothExportDTO } from '@model/jawExportDTO';
import { treatmentCyclesDTO } from '@model/treatmentCyclesDTO';
import { Vertex, Face } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { bufferGeometryToMeshDTO } from '@model/meshDTO';
import { JawSegmentationResponse } from '@/schema/jawSegmentation';
import { ToothSegmentationResponse } from '@/schema/toothSegmentation';
import type { ToothCorrectionResponse } from '@/schema/toothCorrection';
import { lowerFDI, upperFDI } from '@/common/FDI_Notation';
import { JawCorrectionCreate } from '@/schema/JawCorrection';
import ToothContactOverlay from '@correction/three/ToothContactOverlay.ts';
import { getMesialDistalNeighbors } from '@correction/three/toothContactUtils.ts';
import InitialPoseGhosts from '@correction/three/InitialPoseGhosts.ts';
import ArapDeformer from '@three/common/halfEdgeMesh/operations/ArapDeformer';
import { ArapWorkerClient } from '@three/common/halfEdgeMesh/operations/ArapWorkerClient';
import {
  sealOuterBorderLoop,
  type SealOuterLoopOptions,
  type SealOuterLoopResult,
} from '@three/common/halfEdgeMesh/operations/SealOuterBorderLoop';

export type FusedShellExportOptions = {
  /**
   * Delete the interproximal geometry segmentation generated for the SCAN
   * positions and rebuild it between the posed crowns. On by default; turn it
   * off to ship the model exactly as segmentation drew it.
   */
  rebuildInterproximal?: boolean;
  /**
   * Keep the interdental gum joined (the bridges segmentation stitched across
   * the papillae). Off leaves the embrasures open under the contact point,
   * which is the shape the thermoformed foil grips best — but the printed
   * model then has a see-through gap there. On by default.
   */
  joinInterdentalGum?: boolean;
  /**
   * How the hollow's INNER wall is built. 'levelset' is the standard
   * construction every other tool uses — the iso-surface of a distance field
   * (see levelSetOffset.ts) — and the default. 'direct' is the older
   * per-vertex offset with its repair passes, kept so the two can be compared
   * on the same jaw.
   *
   * 'voxel' marches the cavity out of the same field instead of sampling it
   * onto the outer topology, so it can close where the body is thinner than
   * two walls rather than fake it — see voxelInterior. Needs the mesh skirt,
   * for the base plane the ring is triangulated on.
   *
   * The outer surface is identical in all three.
   */
  interior?: 'levelset' | 'direct' | 'voxel';
  /**
   * Stop before hollowing and ship the fused OUTER sheet as it stands: gum and
   * crowns joined into one surface, the interproximal space rebuilt between
   * the posed crowns, the skirt run down to the base plane — and no thickness
   * at all.
   *
   * It is a SHEET, not a solid, and that is the point: it is byte for byte
   * what `buildOffsetShell` is handed, so any other way of giving it volume
   * can be tried on the identical input. `interior` is then unused.
   */
  outerOnly?: boolean;
};

export type JawCorrectionOptions = {
  /**
   * The gum handed in is the one a saved correction stored — already deformed
   * around crowns in their treated pose. Skips the boundary snap the interactive
   * path runs at construction, which would pull that deformation back onto the
   * scan-position crowns. Pair it with `applyToothCorrections` to put the crowns
   * where the gum already expects them.
   */
  gumAlreadyPosed?: boolean;
};

export default class JawCorrection extends HalfEdgeMesh implements RaycastEvents {
  public teeth: ToothCorrection[] = [];
  private treatmentCyclesTransformation = new TreatmentCyclesTransformation();
  private jawsCorrection: JawsCorrection = null!;
  private readonly jawType: 'upper' | 'lower';
  // Optional cap for gum→tooth connection distance (world units). If null, use heuristic.
  private gumToothConnectMaxDistance: number | null = null;
  /** View-only skirt down to the print's base plane; see refreshBasePreview. */
  private basePreview: Mesh | null = null;
  private basePreviewWarned = false;
  // Track which tooth currently owns which gum boundary vertex
  private gumBoundaryToTooth = new Map<Vertex, number>();
  private gumBoundaryToToothVertex = new Map<Vertex, Vertex>();
  private alignedBoundaryVerticesByTooth = new Map<number, Set<Vertex>>();
  // Binding: gum vertex -> local position in its owning tooth space (used to move with tooth)
  private gumVertexLocalInTooth = new Map<Vertex, Vector3>();
  // Snapshot of initial gum vertex positions so animations can start from an undeformed mesh
  private initialGumVertexPositions = new Map<Vertex, Vector3>();
  // How far the gum deformation propagates from the seam (in rings of adjacency)
  private kRingPropagation = 8;
  // Falloff strength for neighbor propagation (p>1 = stronger near seam)
  private propagationExponent = 1;
  // Do not move vertices within this many rings around OTHER teeth seams during propagation
  private otherSeamProtectedRings = 0;
  // Dynamic scaling of propagation based on tooth move magnitude
  private propagationMoveMax = 3.0; // world units at which scaling saturates
  private propagationRingScale = 0.5; // up to +75% more rings
  private propagationExponentScale = 1.2; // up to +80% stronger near seam
  private contactOverlay = new ToothContactOverlay(this);
  private initialPoseGhosts = new InitialPoseGhosts(this);
  // ARAP (As-Rigid-As-Possible) gum deformation, applied on transform-end / settle.
  // The legacy ring-diffusion (updateGumVerticesAttachedToTooth) stays as the live
  // preview during gizmo drag; this produces the clean final result.
  private arap: ArapDeformer | null = null;
  private useArap = true;
  private arapNeedsRebind = true;
  // Off-main-thread ARAP solve for the interactive (release) path. Playback uses the
  // synchronous path so the gum is settled before the next step / export.
  private arapWorker: ArapWorkerClient | null = null;
  private useArapWorker = true;
  private arapSolveToken = 0;
  /**
   * Index of the first face segmentation GENERATED on the GUM — the
   * interdental bridges it stitched across the papillae. Same contract as the
   * crowns' own marker (see ToothCorrection.generatedFacesFrom); the print
   * export deletes it when the interdental gum is not wanted joined.
   */
  private readonly gumGeneratedFacesFrom: number | null;
  /**
   * The gum's index buffer exactly as the PLY delivered it — see
   * ToothCorrection.plyFaceOrder for why: computeBoundsTree reorders the live
   * index in place, and the marker above is only valid in PLY order.
   */
  private readonly gumPlyFaceOrder: Uint32Array | null;

  public constructor(
    gumModel: BufferGeometry,
    teethModels: BufferGeometry[],
    data: JawSegmentationResponse,
    jawType: 'upper' | 'lower',
    options: JawCorrectionOptions = {},
  ) {
    const gumPlyIndex = gumModel.getIndex();
    const gumPlyFaceOrder = gumPlyIndex ? Uint32Array.from(gumPlyIndex.array) : null;
    super(gumModel);
    this.gumPlyFaceOrder = gumPlyFaceOrder;
    this.material = gumMaterial;
    this.jawType = jawType;
    this.gumGeneratedFacesFrom = data.generatedFacesFrom ?? null;
    this.initializeTeeth(teethModels, data.teethSegmentation, options.gumAlreadyPosed === true);
    this.geometry.computeVertexNormals();
    this.refreshBasePreview();
  }

  /**
   * Sets a maximum distance for mapping gum boundary vertices to tooth vertices (in world units).
   * Call this before reconnecting if you want to override the default heuristic.
   */
  public setGumToToothConnectionMaxDistance(dist: number | null) {
    if (dist === null) {
      this.gumToothConnectMaxDistance = null;
    } else {
      this.gumToothConnectMaxDistance = Math.max(1e-6, dist);
    }
  }

  private initializeTeeth(
    teethModels: BufferGeometry[],
    data: ToothSegmentationResponse[],
    gumAlreadyPosed: boolean,
  ) {
    teethModels.forEach((toothModel, index) => {
      this.teeth.push(new ToothCorrection(toothModel, data[index], this));
    });
    // Ensure world matrices reflect the newly added objects before mapping
    this.rootState.scene.updateMatrixWorld(true);
    // Needs every tooth of the jaw in place (neighbor lookup), and must also
    // run for the saved-correction path below — hence before the early return.
    this.buildOrthoFrames();
    if (gumAlreadyPosed) {
      // The gum came out of a SAVED correction: it has already been deformed to
      // follow crowns that have not been moved back yet. Snapping it here would
      // drag the seam onto the scan-position teeth and undo that deformation.
      // The caller places the crowns itself (applyToothCorrections) and never
      // deforms the gum again, so none of the binding below is needed.
      return;
    }
    // Build initial mapping and snap gum boundary onto teeth
    this.mapGumBoundaryVerticesToTeeth();
    this.snapMappedGumVerticesToTeeth();
    // Freeze binding so gum vertices move rigidly with the owning tooth
    this.bindGumVerticesToTeethLocal();
    this.captureInitialGumPositions();
  }

  /**
   * Builds each tooth's orthodontic frame from the INITIAL pose: Y along the
   * long axis (toward the segmentation camera position, i.e. occlusally),
   * X the mesiodistal arch tangent from the neighbors' positions, Z = X×Y
   * (buccolingual). The frame re-orients the gizmo pivot and is what the
   * readout and the treatment-cycle limits are expressed in.
   */
  private buildOrthoFrames() {
    for (const tooth of this.teeth) {
      const origin = tooth.getInitialPosition();
      const yAxis = tooth.getCentralAxis().clone().sub(origin).normalize();
      if (yAxis.lengthSq() < 0.5) {
        // Degenerate segmentation data — leave the identity frame in place.
        console.warn(`buildOrthoFrames: tooth ${tooth.getFDI()} has no usable long axis`);
        continue;
      }

      const neighbors = getMesialDistalNeighbors(tooth, this.teeth, this.jawType);
      let rawTangent: Vector3;
      if (neighbors.mesial && neighbors.distal) {
        rawTangent = neighbors.mesial.getInitialPosition().clone().sub(neighbors.distal.getInitialPosition());
      } else if (neighbors.mesial) {
        rawTangent = neighbors.mesial.getInitialPosition().clone().sub(origin);
      } else if (neighbors.distal) {
        rawTangent = origin.clone().sub(neighbors.distal.getInitialPosition());
      } else {
        // Lone tooth: fall back to the world axis least aligned with Y.
        rawTangent =
          Math.abs(yAxis.x) <= Math.abs(yAxis.z) ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
      }

      // Project the tangent perpendicular to the long axis.
      const xAxis = rawTangent.sub(yAxis.clone().multiplyScalar(rawTangent.dot(yAxis)));
      if (xAxis.lengthSq() < 1e-12) {
        console.warn(`buildOrthoFrames: tooth ${tooth.getFDI()} has a degenerate arch tangent`);
        continue;
      }
      xAxis.normalize();
      const zAxis = new Vector3().crossVectors(xAxis, yAxis);

      const frame = new Quaternion().setFromRotationMatrix(new Matrix4().makeBasis(xAxis, yAxis, zAxis));
      tooth.setOrthoFrame(frame);
    }
  }

  /**
   * Recomputes mapping between gum boundary and teeth using the current or provided max distance.
   */
  public reconnectTeethToGumBoundary(maxDistance?: number) {
    if (typeof maxDistance === 'number') {
      this.setGumToToothConnectionMaxDistance(maxDistance);
    }
    const gumVertices = this.getSeamGumVerticesForRemap();
    if (gumVertices.length === 0) return;
    this.assignGumVerticesToClosestToothBorders(gumVertices, maxDistance);
    this.captureInitialGumPositions();
  }

  /**
   * Sets the gum boundary indices from a loaded correction.
   * This allows restoring the boundary definitions from the database.
   */
  public setGumBoundaryIndices(indices: number[]) {
    if (!indices || indices.length === 0) return;

    this.gumBoundaryToTooth.clear();
    this.gumBoundaryToToothVertex.clear();
    this.alignedBoundaryVerticesByTooth.clear();
    
    const indexSet = new Set(indices);
    const gumVerticesToMap: Vertex[] = [];
    
    this.halfEdges.vertices.forEach(v => {
      if (indexSet.has(v.renderId)) {
        gumVerticesToMap.push(v);
      }
    });
    
    if (gumVerticesToMap.length === 0) {
      console.warn('setGumBoundaryIndices: No matching vertices found for provided indices.');
      return;
    }
    
    this.mapSpecificGumVerticesToTeeth(gumVerticesToMap);
  }
  
  /**
   * Helper to map a specific set of gum vertices to teeth.
   * Similar to mapGumBoundaryVerticesToTeeth but takes an explicit list from persisted renderIds.
   */
  private mapSpecificGumVerticesToTeeth(vertices: Vertex[]) {
    if (vertices.length === 0 || this.teeth.length === 0) return;
    this.assignGumVerticesToClosestToothBorders(vertices);
    this.snapMappedGumVerticesToTeeth();
    this.bindGumVerticesToTeethLocal();
  }

  /**
   * Recomputes ownership map for gum border vertices using the same border-to-border
   * snap contract as segmentation.
   */
  public mapGumBoundaryVerticesToTeeth(maxDistance?: number) {
    this.calculateBorderVertices();
    const border = this.getBorderVertices();
    if (border.length === 0 || this.teeth.length === 0) return;

    this.assignGumVerticesToClosestToothBorders(border, maxDistance);
  }

  /**
   * Segmentation snaps against tooth border verts before fill; correction loads filled
   * tooth PLYs that are often closed manifolds with no topological border.
   */
  private getToothVerticesForGumSnap(tooth: ToothCorrection): Vertex[] {
    tooth.calculateBorderVertices();
    const border = tooth.getBorderVertices();
    if (border.length > 0) return border;
    return [...tooth.halfEdges.vertices];
  }

  /** Snap candidates per tooth — computed once per assign pass. */
  private buildToothSnapCandidates(): Vertex[][] {
    return this.teeth.map((tooth) => this.getToothVerticesForGumSnap(tooth));
  }

  /** Gum seam vertices to re-run assignment (persisted seam or jaw border). */
  private getSeamGumVerticesForRemap(): Vertex[] {
    if (this.alignedBoundaryVerticesByTooth.size > 0) {
      return Array.from(this.alignedBoundaryVerticesByTooth.values()).flatMap((set) => Array.from(set));
    }
    if (this.gumBoundaryToTooth.size > 0) {
      return Array.from(this.gumBoundaryToTooth.keys());
    }
    this.calculateBorderVertices();
    return this.getBorderVertices();
  }

  private assignGumVerticesToClosestToothBorders(gumVertices: Vertex[], maxDistance?: number) {
    const meanEdgeLen = Math.max(this.halfEdges.getMeanEdgeLength?.() ?? 0.05, 1e-6);
    const maxDist = Math.max(
      typeof maxDistance === 'number'
        ? maxDistance
        : this.gumToothConnectMaxDistance ?? meanEdgeLen * 4,
      1e-6,
    );

    this.gumBoundaryToTooth.clear();
    this.gumBoundaryToToothVertex.clear();
    this.alignedBoundaryVerticesByTooth.clear();

    const toothCandidates = this.buildToothSnapCandidates();
    const teethVerticesAroundGum = this.teeth.map(() => new Map<toothVertex, gumVertex>());
    const toothVertexBestDistSq = this.teeth.map(() => new Map<Vertex, number>());

    const positionKey = (p: Vector3) =>
      `${Math.round(p.x * 100)}:${Math.round(p.y * 100)}:${Math.round(p.z * 100)}`;
    const occupiedSnapPositions = new Set<string>();

    for (const gv of gumVertices) {
      const gpWorld = this.localToWorld(gv.position.clone());
      let bestToothVertex: Vertex | null = null;
      let bestTooth = -1;
      let bestDist = maxDist;
      let bestSnapKey: string | null = null;

      for (let ti = 0; ti < this.teeth.length; ti++) {
        const tooth = this.teeth[ti];
        for (const toothVertex of toothCandidates[ti]) {
          const toothPosWorld = tooth.getVertexWorldPosition(toothVertex);
          const dist = gpWorld.distanceTo(toothPosWorld);
          if (dist >= bestDist) continue;

          const snapLocal = this.worldToLocal(toothPosWorld.clone());
          const key = positionKey(snapLocal);
          if (occupiedSnapPositions.has(key)) continue;

          bestDist = dist;
          bestTooth = ti;
          bestToothVertex = toothVertex;
          bestSnapKey = key;
        }
      }

      if (bestTooth >= 0 && bestToothVertex && bestSnapKey) {
        occupiedSnapPositions.add(bestSnapKey);
        this.gumBoundaryToTooth.set(gv, bestTooth);
        this.gumBoundaryToToothVertex.set(gv, bestToothVertex);
        let set = this.alignedBoundaryVerticesByTooth.get(bestTooth);
        if (!set) {
          set = new Set<Vertex>();
          this.alignedBoundaryVerticesByTooth.set(bestTooth, set);
        }
        set.add(gv);

        const distSq = bestDist * bestDist;
        const perToothDist = toothVertexBestDistSq[bestTooth];
        const prevDistSq = perToothDist.get(bestToothVertex);
        if (prevDistSq === undefined || distSq < prevDistSq) {
          perToothDist.set(bestToothVertex, distSq);
          teethVerticesAroundGum[bestTooth].set(bestToothVertex as toothVertex, gv as gumVertex);
        }
      }
    }

    this.teeth.forEach((tooth, index) => {
      tooth.setVerticesAroundGum(teethVerticesAroundGum[index]);
    });

    // Seam invariant check: segmentation equalizes both rims into the same
    // polyline, so every mapped pair should be EXACT (distance ~0). Pairs
    // mapped by proximity instead mean the invariant broke somewhere between
    // segmentation and here — the export will split T-junctions there.
    let exact = 0;
    for (const [gv, ti] of this.gumBoundaryToTooth) {
      const toothVertex = this.gumBoundaryToToothVertex.get(gv);
      const tooth = this.teeth[ti];
      if (!toothVertex || !tooth) continue;
      const distance = this.localToWorld(gv.position.clone()).distanceTo(
        tooth.getVertexWorldPosition(toothVertex),
      );
      if (distance <= 1e-3) exact++;
    }
    const mapped = this.gumBoundaryToTooth.size;
    console.log(
      `assignGumVerticesToClosestToothBorders: ${mapped} gum↔tooth pairs, ${exact} exact` +
        (mapped > exact ? ` — ${mapped - exact} PROXIMITY-ONLY (seam not equalized there)` : ''),
    );
  }

  /**
   * Moves each mapped gum border vertex exactly to its assigned tooth border vertex.
   */
  public snapMappedGumVerticesToTeeth() {
    if (this.gumBoundaryToTooth.size === 0) return;
    for (const [gv, ti] of this.gumBoundaryToTooth) {
      const tooth = this.teeth[ti];
      const toothVertex = this.gumBoundaryToToothVertex.get(gv);
      if (!tooth || !toothVertex) continue;
      const hitWorld = tooth.getVertexWorldPosition(toothVertex);
      const bestLocal = this.worldToLocal(hitWorld.clone());
      this.moveVertex(gv, bestLocal);
    }
    this.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * After mapping and snapping once, compute and store each gum vertex position in its tooth-local space.
   * This allows rigid following (no re-snapping) on subsequent tooth moves.
   */
  private bindGumVerticesToTeethLocal() {
    this.gumVertexLocalInTooth.clear();
    for (const [gv, ti] of this.gumBoundaryToTooth) {
      const tooth = this.teeth[ti];
      const gpWorld = this.localToWorld(gv.position.clone());
      const gpInToothLocal = tooth.worldToLocal(gpWorld.clone());
      this.gumVertexLocalInTooth.set(gv, gpInToothLocal.clone());
    }
  }

  private captureInitialGumPositions() {
    this.initialGumVertexPositions.clear();
    for (const vertex of this.halfEdges.vertices) {
      this.initialGumVertexPositions.set(vertex, vertex.position.clone());
    }
    // Rest pose changed → the cotangent Laplacian must be rebuilt before next solve.
    this.arapNeedsRebind = true;
  }

  /** Enables/disables ARAP gum deformation (vs legacy ring diffusion) for A/B comparison. */
  public setUseArapGumDeformation(value: boolean) {
    this.useArap = value;
  }

  /**
   * (Re)builds the ARAP deformer for the current rest pose and ownership. The whole
   * gum border is the Dirichlet constraint set (seam vertices follow their tooth,
   * the rest of the border is pinned); the interior is solved.
   */
  private bindArap() {
    this.arapNeedsRebind = false;
    if (this.teeth.length === 0 || this.initialGumVertexPositions.size === 0) {
      this.arap = null;
      return;
    }
    const constrained: Vertex[] = [];
    for (const v of this.halfEdges.vertices) {
      if (v.isFree()) constrained.push(v); // whole mesh border
    }
    if (constrained.length === 0) {
      this.arap = null;
      return;
    }
    const deformer = new ArapDeformer();
    deformer.useRotations = true; // ARAP local rotations (handles tooth rotation)
    try {
      deformer.bind(this, this.initialGumVertexPositions, constrained);
      this.arap = deformer;
    } catch (e) {
      console.warn('bindArap: failed to build ARAP deformer, falling back to legacy diffusion', e);
      this.arap = null;
      return;
    }

    // Mirror the bound problem into the worker for the off-main-thread solve path.
    if (this.useArapWorker) {
      if (!this.arapWorker) this.arapWorker = new ArapWorkerClient();
      const problem = this.arap.getProblem();
      if (problem) this.arapWorker.bind(problem);
    }
  }

  /**
   * Builds the mesh-local Dirichlet targets for every gum-border vertex (seam → its
   * tooth, static border → rest) and pins them on the mesh. Returns the constraint map
   * for the ARAP solve, or null if ARAP is unavailable.
   */
  private gatherGumConstraints(): Map<Vertex, Vector3> | null {
    if (!this.useArap) return null;
    if (!this.arap || this.arapNeedsRebind) this.bindArap();
    if (!this.arap) return null;

    const constraints = new Map<Vertex, Vector3>();
    for (const [gv, ti] of this.gumBoundaryToTooth) {
      const tooth = this.teeth[ti];
      const local = this.gumVertexLocalInTooth.get(gv);
      if (!tooth || !local) continue;
      const world = tooth.localToWorld(local.clone());
      constraints.set(gv, this.worldToLocal(world)); // worldToLocal mutates in place
    }
    for (const v of this.halfEdges.vertices) {
      if (!v.isFree() || this.gumBoundaryToTooth.has(v)) continue; // static border only
      const rest = this.initialGumVertexPositions.get(v);
      if (rest) constraints.set(v, rest.clone());
    }
    // Pin the border exactly on the mesh (the solver only moves the free interior).
    for (const [v, p] of constraints) this.moveVertex(v, p);
    return constraints;
  }

  private writeFreePositionsToMesh(apply: (cb: (v: Vertex, x: number, y: number, z: number) => void) => void) {
    const tmp = new Vector3();
    apply((v, x, y, z) => {
      tmp.set(x, y, z);
      this.moveVertex(v, tmp);
    });
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
    // The rim just moved with the gum, so the skirt hanging off it is stale.
    // This is the one funnel every deformation goes through — both the
    // synchronous settle and the worker's late result — so hooking it here
    // means no caller has to remember.
    this.refreshBasePreview();
  }

  /**
   * Synchronous global ARAP solve over the whole gum (used by playback/settle, where the
   * gum must be finalized before the next step). Interactive releases use the async path.
   */
  public solveGumDeformationArap(options?: { arapIters?: number }) {
    const constraints = this.gatherGumConstraints();
    if (!constraints || !this.arap) return;
    const arap = this.arap;
    arap.solveFrame(constraints, { arapIters: options?.arapIters ?? 10 });
    this.writeFreePositionsToMesh((cb) => arap.applyFreePositions(cb));
  }

  /**
   * ARAP solve on a worker (interactive release path). The gum border is pinned
   * immediately; the relaxed interior is applied when the worker returns (~one frame
   * later). Stale results from a superseded release are ignored. Falls back to the
   * synchronous solve if the worker is unavailable.
   */
  public solveGumDeformationArapAsync(options?: { arapIters?: number }) {
    const constraints = this.gatherGumConstraints();
    if (!constraints || !this.arap) return;
    const arap = this.arap;
    const opts = { arapIters: options?.arapIters ?? 10 };

    const promise = this.arapWorker?.solve(arap.packConstraints(constraints), opts);
    if (!promise) {
      // No worker → synchronous fallback.
      arap.solveFrame(constraints, opts);
      this.writeFreePositionsToMesh((cb) => arap.applyFreePositions(cb));
      return;
    }

    const token = ++this.arapSolveToken;
    void promise.then((free) => {
      if (token !== this.arapSolveToken) return; // a newer release superseded this one
      this.writeFreePositionsToMesh((cb) => arap.applyFreePositionsFromArray(free, cb));
    });
  }

  public resetGumToInitialPositions(options?: { rebind?: boolean }) {
    const { rebind = true } = options ?? {};
    if (this.initialGumVertexPositions.size === 0) return;
    this.initialGumVertexPositions.forEach((initialPosition, vertex) => {
      this.moveVertex(vertex, initialPosition.clone());
    });
    this.geometry.attributes.position.needsUpdate = true;
    if (rebind) {
      this.bindGumVerticesToTeethLocal();
    }
  }

  /**
   * Restores teeth and gum to the stored initial transforms and updates the binding maps.
   * This is used before playback so gum vertices bind against the original tooth poses.
   */
  public resetToInitialTransforms() {
    this.resetGumToInitialPositions({ rebind: false });

    this.teeth.forEach((tooth) => {
      const initialPosition = tooth.getInitialPosition().clone();
      const initialQuaternion = tooth.getInitialQuaternion();
      tooth.setPosition(initialPosition);
      tooth.setRotationFromQuaternion(initialQuaternion);
      tooth.rotateHalfEdges(initialQuaternion, tooth.getPivotLocation());
    });

    // Ensure world matrices match the restored transforms before re-binding
    this.rootState.scene.updateMatrixWorld(true);

    this.bindGumVerticesToTeethLocal();
    this.captureInitialGumPositions();

    this.teeth.forEach((tooth) => {
      this.updateGumVerticesAttachedToTooth(tooth);
    });

    this.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Update positions of gum vertices owned by a given tooth by applying the tooth transform
   * to their stored local positions (no closest-point snapping).
   */
  public updateGumVerticesAttachedToTooth(tooth: ToothCorrection) {
    const toothIndex = this.teeth.indexOf(tooth);
    if (toothIndex < 0) return;
    const set = this.alignedBoundaryVerticesByTooth.get(toothIndex);
    if (!set) return;
    
    // 1) Move seam (owned) vertices exactly to their bound tooth-local positions
    let dpSum = new Vector3();
    let dpCount = 0;
    const seedWorldTargets: Map<Vertex, Vector3> = new Map();
    const seedDp: Map<Vertex, Vector3> = new Map();
    for (const gv of set) {
      const local = this.gumVertexLocalInTooth.get(gv);
      if (!local) continue;
      const currentWorld = this.localToWorld(gv.position.clone());
      const worldTarget = tooth.localToWorld(local.clone());
      seedWorldTargets.set(gv, worldTarget.clone());
      const dp = worldTarget.clone().sub(currentWorld);
      seedDp.set(gv, dp.clone());
      dpSum.add(dp);
      dpCount++;
      const jawLocal = this.worldToLocal(worldTarget.clone());
      this.moveVertex(gv, jawLocal);
    }

    // 2) Compute average displacement for a simple, stable propagation to K-ring neighbors
    if (dpCount > 0 && this.kRingPropagation > 0) {
      const dpAvg = dpSum.multiplyScalar(1 / dpCount);

      // Dynamic propagation strength based on how far this tooth moved from its initial position
      const moveLen = tooth.getMovedPosition().length();
      const moveNorm = Math.max(0, Math.min(1, moveLen / this.propagationMoveMax));
      const maxRing = Math.max(1, Math.round(this.kRingPropagation * (1 + this.propagationRingScale * moveNorm)));
      const expUse = this.propagationExponent * (1 + this.propagationExponentScale * moveNorm);

      // Build a protected set: up to N-ring neighbors around seams of OTHER teeth.
      // These vertices must not be included in propagation to avoid tugging near other teeth.
      const protectedSet = new Set<Vertex>();
      const protectRings = Math.max(0, Math.floor(this.otherSeamProtectedRings));
      if (protectRings > 0) {
        for (let ti = 0; ti < this.teeth.length; ti++) {
          if (ti === toothIndex) continue;
          const seamOther = this.alignedBoundaryVerticesByTooth.get(ti);
          if (!seamOther || seamOther.size === 0) continue;
          const queueProt: Array<{ v: Vertex; r: number }> = [];
          for (const sv of seamOther) {
            // Ignore vertices shared/owned by the current tooth
            const ownerSv = this.gumBoundaryToTooth.get(sv);
            if (ownerSv === toothIndex || set.has(sv)) continue;
            if (!protectedSet.has(sv)) {
              protectedSet.add(sv);
              queueProt.push({ v: sv, r: 0 });
            }
          }
          while (queueProt.length > 0) {
            const { v, r } = queueProt.shift()!;
            if (r >= protectRings) continue;
            for (const n of v.adjacentVertices()) {
              // Ignore vertices shared/owned by the current tooth
              const ownerN = this.gumBoundaryToTooth.get(n);
              if (ownerN === toothIndex || set.has(n)) continue;
              if (!protectedSet.has(n)) {
                protectedSet.add(n);
                queueProt.push({ v: n, r: r + 1 });
              }
            }
          }
        }
      }

      // Multi-source BFS from all seeds to compute ring distances up to K
      const ringMap = new Map<Vertex, number>();
      const queue: Array<{ v: Vertex; r: number }> = [];
      for (const gv of set) {
        ringMap.set(gv, 0);
        for (const n of gv.adjacentVertices()) {
          const owner = this.gumBoundaryToTooth.get(n);
          if (owner !== undefined && owner !== toothIndex) continue; // do not step onto other-tooth seam
          if (protectedSet.has(n)) continue; // protect adjacency of other seams
          if (!ringMap.has(n)) {
            ringMap.set(n, 1);
            queue.push({ v: n, r: 1 });
          }
        }
      }
      while (queue.length > 0) {
        const { v, r } = queue.shift()!;
        if (r >= maxRing) continue;
        for (const n of v.adjacentVertices()) {
          const owner = this.gumBoundaryToTooth.get(n);
          if (owner !== undefined && owner !== toothIndex) continue; // block crossing to other-tooth seam
          if (protectedSet.has(n)) continue; // protect adjacency of other seams
          if (!ringMap.has(n)) {
            const nr = r + 1;
            ringMap.set(n, nr);
            if (nr <= maxRing) queue.push({ v: n, r: nr });
          }
        }
      }

      // Build displacement field that respects rotation: diffuse seam dps outward by ring
      const dpField = new Map<Vertex, Vector3>();
      // seeds have exact dp
      for (const gv of set) dpField.set(gv, seedDp.get(gv) ?? dpAvg);
      // propagate ring by ring
      for (let r = 1; r <= maxRing; r++) {
        for (const [v, rv] of ringMap) {
          if (rv !== r) continue;
          if (protectedSet.has(v)) continue;
          // average dp from neighbors with smaller ring
          let sum = new Vector3();
          let cnt = 0;
          for (const n of v.adjacentVertices()) {
            const rn = ringMap.get(n);
            if (rn === undefined || rn >= r) continue;
            const dpn = dpField.get(n);
            if (!dpn) continue;
            sum.add(dpn);
            cnt++;
          }
          const dpv = cnt > 0 ? sum.multiplyScalar(1 / cnt) : dpAvg;
          dpField.set(v, dpv);
        }
      }

      // Apply displacement with ring-based falloff to neighbors (exclude seeds at r=0)
      for (const [v, r] of ringMap) {
        if (r === 0) continue; // seeds already set exactly
        if (r > maxRing) continue;
        if (protectedSet.has(v)) continue; // do not move protected vertices
        const w = this.weightForRing(r, maxRing, expUse);
        if (w <= 0) continue;
        const currWorld = this.localToWorld(v.position.clone());
        const dpv = dpField.get(v) ?? dpAvg;
        const newWorld = currWorld.addScaledVector(dpv, w);
        const jawLocal = this.worldToLocal(newWorld.clone());
        this.moveVertex(v, jawLocal);
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
  }

  /** Adjust how far gum deformation propagates from seam (k-ring). */
  public setGumPropagationRing(k: number) {
    this.kRingPropagation = Math.max(0, Math.floor(k));
  }

  /** Configure how many rings around OTHER seams are protected from propagation. */
  public setOtherSeamProtectedRings(k: number) {
    this.otherSeamProtectedRings = Math.max(0, Math.floor(k));
  }

  private weightForRing(r: number, k: number, expUse?: number): number {
    // Power falloff: larger exponent -> stronger effect near the seam
    const x = r / (k + 1);
    const p = typeof expUse === 'number' ? expUse : this.propagationExponent;
    return Math.max(0, 1 - Math.pow(x, p));
  }

  /** Adjust falloff exponent (default 2.0). Higher => stronger near seam, quicker decay. */
  public setGumPropagationFalloffExponent(p: number) {
    this.propagationExponent = Math.max(0.1, p);
  }

  /** Convenience: map and snap in one call */
  public remapAndSnapGumVertices(maxDistance?: number) {
    this.mapGumBoundaryVerticesToTeeth(maxDistance);
    this.snapMappedGumVerticesToTeeth();
    this.bindGumVerticesToTeethLocal();
  }

  /**
   * Backwards-compatible name for the correction snap step.
   * Correction now follows segmentation: gum border vertices snap to tooth border vertices.
   */
  public alignGumBoundaryToTeethSurface(maxDistance?: number) {
    this.remapAndSnapGumVertices(maxDistance);
  }

  /** Reprojects existing owned gum vertices to their assigned tooth border vertices. */
  public realignOwnedGumVertices() {
    this.snapMappedGumVerticesToTeeth();
    this.bindGumVerticesToTeethLocal();
  }

  public raycastHit(intersection: Intersection<Object3D>) {
    if (!intersection.face) {
      return;
    }
  }

  /**
   * Test hook: seal the gum's outer free border with a flat cap (collapse → smooth →
   * flatten → fill). The fill rebuilds the half-edge DS, replacing all Vertex references,
   * so the gum→tooth seam binding is rebuilt afterwards to keep deformation working.
   */
  /**
   * Binary STL of the whole jaw model as currently posed: sealed gum plus every
   * tooth, each baked with its world transform. Built from fresh clones so
   * gizmos/pivots hanging in the scene never leak into the export.
   */
  public exportModelToSTL(): DataView | string {
    const group = new Group();
    const addClone = (mesh: Mesh) => {
      mesh.updateWorldMatrix(true, false);
      const clone = new Mesh(mesh.geometry);
      clone.matrixAutoUpdate = false;
      clone.matrix.copy(mesh.matrixWorld);
      group.add(clone);
    };
    addClone(this);
    for (const tooth of this.teeth) addClone(tooth);
    group.updateMatrixWorld(true);
    return MeshUtils.exportMeshToSTL(group);
  }

  /**
   * Binary STL of the jaw as a hollow, open-bottom shell for material-saving
   * prints — built VOLUMETRICALLY so the result is manifold, watertight (up
   * to the open bottom) and self-intersection-free, which slicers require:
   *
   *   1. seal the gum rim flat WITH a cap → a closed solid boundary,
   *   2. fuse gum + posed teeth into ONE connected sheet (weld coincident
   *      seam vertices, split T-junction chords) — seams become interior
   *      topology instead of free rims,
   *   3. narrow-band RAW signed distance of the fused sheet (free rims — outer
   *      perimeter, open interproximal windows — end in a flush cut),
   *   4. one-cell 26-connected closing → welds sub-voxel field pinholes
   *      (doubled scan layers) without touching the interdental spaces,
   *   5. deliberate interproximal blockout on the SOLID — gaps between
   *      neighbouring crowns whose local width is under the threshold are
   *      bridged with a smooth web; wider embrasures stay as scanned,
   *   6. hollow-shell slab (0.5 mm wall behind the surface) — because it runs
   *      AFTER the blockout, the web is hollowed like any other surface,
   *   7. cut everything past the rim plane → open bottom + open cavity mouth,
   *   8. marching cubes + weld, then Taubin smoothing followed by a
   *      sharp-feature-masked reprojection onto the fused sheet — the
   *      smoothing kills the voxel terracing, the reprojection puts cusps,
   *      incisal edges and the cervical margin back on the scan (see
   *      projectOntoSource). This is what decouples surface fidelity from the
   *      cell size. The interproximal CHANNELS get the opposite treatment —
   *      extra masked smoothing and no reprojection — because the seam debris
   *      and marching-cubes lace live there and the foil never pulls in.
   *
   * @param thicknessMm      wall thickness of the hollow shell.
   * @param blockoutGapMm    interproximal blockout threshold. The 0.6 mm
   *   default suits passive appliances (retention foils), where a bridged
   *   narrow embrasure is better than a ragged one. An ALIGNER needs the
   *   embrasures open to grip each crown separately — pass a small value
   *   (~0.3 mm, closing only what the voxels cannot resolve) or 0.
   *
   * NOTE: flattens and caps the live gum mesh (same mutation as sealOuterGumLoop).
   */
  public async exportHollowModelToSTL(
    thicknessMm = 0.5,
    blockoutGapMm = 0.1,
  ): Promise<DataView | string> {
    const stage = (name: string) => console.log(`exportHollowModelToSTL (${this.jawType}): ${name}`);
    stage('seal/flatten + cap');
    const seal = this.sealOuterGumLoop();
    if (seal.planeY === undefined) {
      throw new Error('exportHollowModelToSTL: rim plane could not be resolved');
    }

    // Diagnostic: what stays open on the gum after the seal. The tooth-socket
    // rims are expected (crowns cover them); a LARGE extra loop means the gum
    // is an open band (e.g. the lingual edge after the review trim) — handled
    // by the geometric shell, but worth seeing when debugging exports.
    {
      const loops = this.getBorderLoops();
      const perimeters = loops
        .map((loop) => {
          let length = 0;
          for (let i = 0; i < loop.length; i++) {
            length += loop[i].position.distanceTo(loop[(i + 1) % loop.length].position);
          }
          return length;
        })
        .sort((p, q) => q - p);
      console.log(
        `exportHollowModelToSTL (${this.jawType}): gum border loops after seal: ${loops.length}, ` +
          `perimeters [${perimeters.slice(0, 6).map((p) => p.toFixed(1)).join(', ')}${perimeters.length > 6 ? ', …' : ''}] mm`,
      );
    }

    const bakeWorldPositions = (mesh: Mesh): BufferGeometry => {
      mesh.updateWorldMatrix(true, false);
      return mesh.geometry.clone().applyMatrix4(mesh.matrixWorld);
    };

    stage('bake parts');
    const parts = [this as Mesh, ...this.teeth].map(bakeWorldPositions);

    // Dev dump (?dev in the URL): download every baked part as PLY so a failing
    // real-world export can be replayed through the pipeline in a test. Dumped
    // BEFORE tucking so the replay starts from the pristine parts.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('dev')) {
      stage('dev dump of baked parts');
      for (let i = 0; i < parts.length; i++) {
        const blob = await geometryToPly(parts[i]);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `hollow-${this.jawType}-part-${String(i).padStart(2, '0')}${i === 0 ? '-gum' : '-tooth'}.ply`;
        anchor.click();
        URL.revokeObjectURL(url);
      }
    }

    // Fuse the parts into ONE topologically connected sheet: weld the
    // coincident seam vertices (gum socket rims are snapped onto tooth rim
    // vertices and follow them rigidly) and split the T-junction chords left
    // by the density mismatch between the two rims. Seams become interior
    // topology, so the shell field sees no free rims along the gum line or the
    // interproximal seams — the source of every bead/notch artifact there.
    // Interproximal windows genuinely opened by tooth movement and the outer
    // perimeter stay open, which is what the flush-cut slab handles cleanly.
    stage('fuse seams');
    const { geometry: fused, stats: fusion } = fuseSeamTJunctions(parts);
    console.log(`exportHollowModelToSTL (${this.jawType}): seam fusion ${JSON.stringify(fusion)}`);

    // Voxel size: target ~35M voxels (a typical jaw lands on the 0.15 mm
    // floor, ~140 MB of field). The cell size no longer sets the surface
    // fidelity — the reprojection pass after marching cubes puts the outer
    // surface back onto this fused sheet, so what it still governs is which
    // gaps survive as gaps (blockoutGapMm closes the rest deliberately), the
    // robustness of the ~3-cell wall, and the triangle count. Finer cells are
    // only warranted when the blockout threshold drops towards the contacts
    // (an aligner model), which is why the two knobs travel together.
    fused.computeBoundingBox();
    const bb = fused.boundingBox!.clone();
    const pad = 1.5;
    const volume =
      (bb.max.x - bb.min.x + 2 * pad) * (bb.max.y - bb.min.y + 2 * pad) * (bb.max.z - bb.min.z + 2 * pad);
    // Resolution follows the blockout threshold: a gap that is meant to stay
    // OPEN needs ~3 samples across it, and everything below the threshold is
    // bridged deliberately, so a passive-appliance model (0.6 mm) is happy at
    // 0.15 mm while an aligner model (threshold near the contacts) needs
    // 0.10 mm. Floored by a hard ~120 M voxel memory budget.
    const gapCell = Math.min(0.15, Math.max(0.1, blockoutGapMm / 3));
    const cell = Math.min(0.3, Math.max(gapCell, Math.cbrt(volume / 120_000_000)));

    // The flood rasterizer samples triangle interiors — the huge earcut cap
    // triangles must be split below its sampling reach first, or the flood
    // leaks through pinholes and the sign inverts.
    stage('subdivide oversized triangles');
    const soup = subdivideLongTriangles(fused, 1.5);
    // `fused` stays alive: welded and indexed, it is both the reprojection
    // target and the geometry whose dihedral angles define the sharp features
    // worth restoring (the subdivided soup has no usable adjacency).

    stage(`sdf (cell ${cell.toFixed(2)} mm)`);
    const generator = new SDFGenerator([new Mesh(soup)]);
    // RAW union signed distance of the open sheet (openSheet skips the flood's
    // seal escalation — a jaw sheet always "drains" through its rim). The
    // hollow-shell slab is applied AFTERWARDS (applySheetSlab) so the carve
    // below edits the SOLID and its cut faces get walls like any surface.
    // Free rims (outer perimeter, open interproximal windows) still end in a
    // flush cut — see unionSignedDistance.
    const grid = await generator.generateNarrowBandFlood(
      {
        cellSize: cell,
        padding: pad,
        narrowBandMm: thicknessMm + 6 * cell,
        openSheet: true,
        diagnostics: true,
      },
      getSdfWorkerPool(),
    );
    generator.dispose();
    soup.dispose();

    // One-cell 26-connected closing of the SOLID. With the seams fused AND
    // the slits zipped at mesh level, the only defects left are FIELD-level
    // sub-voxel pinholes (doubled scan layers, fissure interference) — one
    // cell of reach welds those without touching the interdental spaces,
    // which keep their original scanned shape.
    stage('close sub-voxel pinholes');
    closeSDFInPlace(grid, 1, '26-connected');

    // Deliberate interproximal blockout: gaps whose LOCAL WIDTH is under the
    // threshold are bridged with a smooth web (the thermoformed foil cannot
    // pull into them anyway, and resolution-limit gaps come out as ragged
    // lace the foil wrinkles on); wider embrasures keep their scanned shape,
    // and no crown surface moves outward. Runs on the SOLID, so the web is
    // hollowed like everything else.
    stage('blockout interproximal webs');
    const filled = blockoutInterproximalWebs(grid, parts.slice(1), blockoutGapMm);
    parts[0].dispose();
    // The crown parts stay alive: the interproximal-channel mask after
    // marching cubes needs them (vertices near TWO different crowns).
    if (filled > 0) {
      console.log(`exportHollowModelToSTL (${this.jawType}): webbed ${filled} interproximal voxels`);
    }

    // Hollow: material becomes the `thicknessMm` slab behind the (carved)
    // solid's surface; the outer face stays exactly on the original geometry.
    stage('hollow shell slab');
    applySheetSlab(grid, thicknessMm);

    stage('open bottom');
    const { origin, counts, strides, cellSize } = grid.info;
    const data = grid.data;
    // Cut away the capped floor slab just past the rim plane → open bottom.
    const planeWorldY = this.localToWorld(new Vector3(0, seal.planeY, 0)).y;
    const cutMargin = thicknessMm + 2 * cellSize;
    const cutY = this.jawType === 'upper' ? planeWorldY - cutMargin : planeWorldY + cutMargin;
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        const wy = origin.y + y * cellSize;
        const sd = this.jawType === 'upper' ? wy - cutY : cutY - wy;
        if (sd <= -cellSize) continue;
        let idx = x * strides.x + y * strides.y;
        for (let z = 0; z < counts.z; z++, idx++) {
          if (sd > data[idx]) data[idx] = sd;
        }
      }
    }

    stage('marching cubes');
    const raw = extractMesh(grid, { computeNormals: false });
    const welded = weldMeshVertices(raw, cellSize * 0.05);
    raw.dispose();

    // The extracted solid is CLOSED by construction, so every boundary edge is
    // a defect: classic marching-cubes face-ambiguity cracks (frequent on thin
    // walls) or a missed weld pair. Fill every boundary loop (keepLargest 0);
    // iterate — loops meeting at non-manifold junctions can need a second pass.
    let cracks = 0;
    for (let pass = 0; pass < 3; pass++) {
      const filled = fillMinorBoundaryLoops(welded, 0);
      if (filled === 0) break;
      cracks += filled;
    }
    if (cracks > 0) {
      console.log(`exportHollowModelToSTL (${this.jawType}): filled ${cracks} crack loop(s)`);
    }

    // Voxel-scale floaters (doubled-scan bubbles, cut slivers) are unprintable
    // and read as errors in slicers — drop them.
    const dropped = removeSmallComponents(welded, 500);
    if (dropped > 0) {
      console.log(`exportHollowModelToSTL (${this.jawType}): removed ${dropped} debris component(s)`);
    }

    // Taubin smoothing INTERLEAVED with reprojection onto the fused sheet.
    //
    // Smoothing alone cannot tell a voxel terrace from a real crease, so it
    // keeps eating the sharpest places — cusp tips, incisal edges, the
    // cervical margin — and that inward drift is exactly what a thermoformed
    // appliance feels (the foil ignores radii under ~0.2 mm, but not a height
    // of contour that moved). Reprojecting after each short burst snaps every
    // vertex still within a cell of the scan back onto it, so the drift never
    // accumulates and fidelity stops depending on the cell size. Vertices
    // farther out — the inner wall, the plane cut, the blockout webs — are
    // deliberately new geometry and keep the smoothed voxel shape.
    stage('smooth + reproject');
    smoothSurface(welded, 12, 0.5, -0.53, thicknessMm / 6);

    // The interproximal channels get the OPPOSITE treatment from the rest of
    // the surface: extra smoothing, and no reprojection. The segmentation seam
    // and its scan-bridge debris run exactly through the channel, gaps of 1–3
    // cells render as marching-cubes lace, and the sharp-feature mask is dense
    // along the seam — so the fit pass would faithfully restore that junk.
    // Fidelity there is also worthless to the appliance: the foil never pulls
    // into a sub-millimetre gap. Channel = vertices within reach of TWO
    // different crowns.
    const teethParts = parts.slice(1);
    const channel = markInterproximalRegion(welded, teethParts, 0.8);
    const fusedChannel = markInterproximalRegion(fused, teethParts, 0.8);
    teethParts.forEach((g) => g.dispose());
    if (channel.flagged > 0) {
      stage(`smooth interproximal channels (${channel.flagged} vertices)`);
      smoothSurface(welded, 16, 0.5, -0.53, thicknessMm / 6, channel.vertices);
    }

    // Fitting comes AFTER all the smoothing, never interleaved: a projection
    // restores the crease, and any smoothing that follows eats the restored
    // crease harder than the chamfer it started from (measured: interleaving
    // ends up worse than not fitting at all). Masked to sharp source features —
    // the cusp ridges, incisal edges and cervical creases an appliance grips —
    // so the scan's own roughness stays smoothed. Two passes: the first is
    // step-clamped, the second finishes what it could not reach.
    const sourceBvh = new MeshBVH(fused);
    // Crease faces only, no dilation: measured on the reference jaw, growing the
    // mask does not improve the sharp band any further (p95 saturates at
    // ~0.083 mm) and only drags the SMOOTH areas back onto the scan — i.e. it
    // restores the roughness the smoothing removed, at 4× the vertices touched.
    // Channel faces are subtracted for the reason above.
    const sharpFaces = markSharpFaces(fused, 50);
    for (let i = 0; i < sharpFaces.length; i++) {
      if (fusedChannel.faces[i]) sharpFaces[i] = 0;
    }
    const projectBand = Math.min(2 * cellSize, thicknessMm * 0.4);
    for (let pass = 0; pass < 2; pass++) {
      const fit = projectOntoSource(welded, sourceBvh, projectBand, cellSize * 0.75, sharpFaces);
      console.log(
        `exportHollowModelToSTL (${this.jawType}): fit pass ${pass + 1} ` +
          `moved ${fit.moved}, reverted ${fit.reverted}, max ${fit.maxMoveMm.toFixed(3)} mm`,
      );
      if (fit.moved === 0) break;
    }
    fused.dispose();
    const smoothed = welded;

    stage('orient for print');
    // Marching cubes emits inward winding for our negative-inside field — flip
    // every triangle so normals point out of the material.
    const smoothedIndex = smoothed.getIndex()!;
    for (let i = 0; i < smoothedIndex.count; i += 3) {
      const tmp = smoothedIndex.getX(i + 1);
      smoothedIndex.setX(i + 1, smoothedIndex.getX(i + 2));
      smoothedIndex.setX(i + 2, tmp);
    }
    // three.js is Y-up, slicers are Z-up. Rotate so the open (plane-cut) rim
    // lands on the build plate: the lower jaw opens downwards (−Y), the upper
    // upwards (+Y), so they need opposite quarter-turns about X.
    smoothed.rotateX(this.jawType === 'upper' ? -Math.PI / 2 : Math.PI / 2);
    smoothed.computeVertexNormals();

    stage('write STL');
    const stl = MeshUtils.exportMeshToSTL(new Mesh(smoothed));
    smoothed.dispose();
    return stl;
  }

  /**
   * Every tooth as its own watertight shell, all of them concatenated into
   * one STL together with the gum as one more standalone body. No merging, no
   * field union: each crown is a standalone open sheet, so interdental spaces
   * cannot fuse and there are no gum seams. Contacts between crowns stay as
   * overlapping separate solids, which a slicer unions.
   *
   * Two ways to get from the sheet to the shell:
   *
   * 'direct' — the outer surface is the crown mesh itself and only the inner
   * wall is generated (see buildOffsetShell). Nothing resamples the anatomy,
   * so nothing has to be put back afterwards, and the inner wall is relaxed
   * onto the surface a distance field would draw instead of being pushed
   * blindly inwards.
   *
   * 'extrude' — the same, but with those guards off: every face pushed in by
   * the full thickness, exactly what Blender's Solidify does. The wall is dead
   * even, and at cusp tips and the interproximal slivers it folds. The log
   * says by how much, per tooth.
   *
   * 'shrink' — no normals at all: the whole mesh scaled about its own centre,
   * which is a similarity and so provably cannot self-intersect (19 folded
   * faces in 113 257, an order of magnitude below anything else). What it
   * costs is thickness — the wall follows distance from the centre, so a
   * twentieth of it comes out at 0.05 mm or less. Compare it in a print
   * before trusting it.
   *
   * 'voxel' — the older route: signed distance field, slab, marching cubes,
   * smoothing, then a masked reprojection to recover what the round trip lost.
   * Kept for comparison; it is the one that turned scan-scale detail into
   * ridges the reprojection then reprinted.
   *
   * The gum ships alongside them as its own standalone hollow solid, built by
   * the voxel route from the gum sheet ALONE (see buildGumShellGeometry) —
   * the per-tooth construction has nothing to offer it, since the gum is one
   * smooth sheet with none of the anatomy the direct path exists to preserve.
   * Its socket rims coincide with the crowns' cervical rims, so in the print
   * the bodies overlap there and the slicer unions them. The rim sealing runs
   * on a throwaway copy — unlike exportHollowModelToSTL, this export does not
   * mutate the live gum, so it can be run repeatedly.
   */
  public async exportPerToothShellsToSTL(
    thicknessMm = 0.5,
    mode: 'direct' | 'extrude' | 'shrink' | 'voxel' = 'direct',
    cellMm = 0.1,
    includeGum = true,
  ): Promise<DataView | string> {
    const stage = (name: string) => console.log(`exportPerToothShellsToSTL (${this.jawType}): ${name}`);
    const shells: BufferGeometry[] = [];
    for (let t = 0; t < this.teeth.length; t++) {
      const tooth = this.teeth[t];
      stage(`tooth ${t + 1}/${this.teeth.length} (${mode})`);
      tooth.updateWorldMatrix(true, false);
      const baked = tooth.geometry.clone().applyMatrix4(tooth.matrixWorld);
      const indexed = weldMeshVertices(baked, 1e-5);
      baked.dispose();

      if (mode !== 'voxel') {
        const shell = buildOffsetShell(indexed, {
          thicknessMm,
          fit: mode === 'extrude' ? 'plain' : mode === 'shrink' ? 'shrink' : 'relaxed',
          // Uniform scale: the variant with the no-self-intersection
          // guarantee. Turning this on steps each vertex far enough along the
          // radius to hold the wall instead, which trades the guarantee for
          // thickness (60 % of the wall on target rather than 23 %).
          ...(mode === 'shrink' ? { evenThickness: false } : {}),
        });
        stage(
          `tooth ${t + 1}: wall median ${shell.medianThicknessMm.toFixed(3)} mm, ` +
            `thinnest ${shell.minThicknessMm.toFixed(3)} mm at ${shell.taperedVertices} of ` +
            `${shell.vertexCount} vertices, p5 ${
              Float32Array.from(shell.thickness).sort()[Math.floor(shell.vertexCount * 0.05)]
                .toFixed(3)
            } mm, ${shell.foldedFaces} folded faces, ` +
            `${shell.outsideVertices} poking out by up to ` +
            `${shell.outsideDepthMm.toFixed(3)} mm, ${shell.borderLoops} rim loop(s)`,
        );
        const open = countOpenEdges(shell.geometry);
        if (open > 0) stage(`tooth ${t + 1}: WARNING ${open} open edges — not a closed solid`);
        indexed.dispose();
        shells.push(shell.geometry);
        continue;
      }

      const soup = subdivideLongTriangles(indexed, 1.5);

      const generator = new SDFGenerator([new Mesh(soup)]);
      const grid = await generator.generateNarrowBandFlood(
        {
          cellSize: cellMm,
          padding: 1.5,
          narrowBandMm: thicknessMm + 6 * cellMm,
          openSheet: true,
        },
        getSdfWorkerPool(),
      );
      generator.dispose();
      soup.dispose();
      closeSDFInPlace(grid, 1, '26-connected');
      applySheetSlab(grid, thicknessMm);

      const raw = extractMesh(grid, { computeNormals: false });
      const welded = weldMeshVertices(raw, cellMm * 0.05);
      raw.dispose();
      for (let pass = 0; pass < 3; pass++) {
        if (fillMinorBoundaryLoops(welded, 0) === 0) break;
      }
      removeSmallComponents(welded, 200);
      smoothSurface(welded, 12, 0.5, -0.53, thicknessMm / 6);

      // The tooth's source is the crown scan PLUS the interproximal wall that
      // segmentation generated over the cut, and those are not the same kind
      // of surface. The wall spans the opening with chords between rim
      // vertices, so its triangles are millimetres wide where the scan's are
      // ~0.15 mm, and its ridges are triangulation, not anatomy. Fitting the
      // shell back onto it reprints every one of them. So the fit is masked to
      // the scan and the wall keeps the smooth shape marching cubes gave it —
      // the same split the fused export makes for interproximal channels.
      const sourceIndex = indexed.getIndex()!;
      const sourcePosition = indexed.getAttribute('position');
      const faceCount = sourceIndex.count / 3;
      const spans = new Float64Array(faceCount);
      const cornerA = new Vector3();
      const cornerB = new Vector3();
      const cornerC = new Vector3();
      for (let f = 0; f < faceCount; f++) {
        cornerA.fromBufferAttribute(sourcePosition, sourceIndex.getX(f * 3));
        cornerB.fromBufferAttribute(sourcePosition, sourceIndex.getX(f * 3 + 1));
        cornerC.fromBufferAttribute(sourcePosition, sourceIndex.getX(f * 3 + 2));
        spans[f] = Math.max(
          cornerA.distanceTo(cornerB),
          cornerB.distanceTo(cornerC),
          cornerC.distanceTo(cornerA),
        );
      }
      const medianSpan = [...spans].sort((a, b) => a - b)[faceCount >> 1] || 0;
      const scanFaces = new Uint8Array(faceCount).fill(1);
      let generated = 0;
      if (medianSpan > 0) {
        for (let f = 0; f < faceCount; f++) {
          if (spans[f] <= medianSpan * 4) continue;
          scanFaces[f] = 0;
          generated++;
        }
      }
      if (generated > 0) {
        stage(`tooth ${t + 1}: ${generated}/${faceCount} generated wall faces held out of the fit`);
      }

      const sourceBvh = new MeshBVH(indexed);
      // Fit onto the SCAN only. 3 passes × step saturate the band (strays can
      // sit a full cell off).
      const band = Math.min(2 * cellMm, thicknessMm * 0.4);
      for (let pass = 0; pass < 3; pass++) {
        projectOntoSource(welded, sourceBvh, band, cellMm * 0.75, generated > 0 ? scanFaces : null);
      }
      // Marching cubes emits inward winding for our negative-inside field, so
      // this branch flips; the direct one already builds its faces outwards.
      const wound = welded.getIndex()!;
      for (let i = 0; i < wound.count; i += 3) {
        const tmp = wound.getX(i + 1);
        wound.setX(i + 1, wound.getX(i + 2));
        wound.setX(i + 2, tmp);
      }
      indexed.dispose();
      shells.push(welded);
    }

    if (includeGum) {
      stage('gum shell (voxel)');
      shells.push(await this.buildGumShellGeometry(thicknessMm));
    }

    stage('merge + orient for print');
    const merged = mergePositionIndexGeometries(shells);
    shells.forEach((s) => s.dispose());
    // three.js is Y-up, slicers are Z-up: put the open cervical rim on the
    // build plate. The lower jaw opens downwards, the upper upwards, so they
    // need opposite quarter-turns about X.
    merged.rotateX(this.jawType === 'upper' ? -Math.PI / 2 : Math.PI / 2);
    merged.computeVertexNormals();

    stage('write STL');
    const stl = MeshUtils.exportMeshToSTL(new Mesh(merged));
    merged.dispose();
    return stl;
  }

  /**
   * The whole jaw as ONE watertight hollow body: gum and posed teeth are
   * fused into a single sheet (the same fusion the voxel export feeds on),
   * the sheet is repaired to a genuine 2-manifold — the step the voxel route
   * never needed, because an SDF is topology-blind where a direct offset
   * inherits every defect — and buildOffsetShell turns it into the shell:
   * outer surface bit-identical to the fused sheet, the inner wall relaxed
   * onto the distance-field offset.
   *
   * `skirtVia` decides how the generated wall from the gum rim down to the
   * base plane is made:
   *
   * 'mesh' — the skirt is extruded into the fused sheet itself and the offset
   * bridges its bottom ring into the flat base; one body, no voxels anywhere.
   *
   * 'voxel' — the hybrid the user asked for: the shell covers the ANATOMY
   * only (its ragged rim bridged as-is), and the skirt is a separate
   * voxel-built body overlapping it near the rim, unioned by the slicer.
   * Voxels are the right tool exactly there: the rim is where the scan is
   * junk and fidelity is worthless, and an SDF is blind to junk — the same
   * blindness that disqualifies it for the crowns.
   *
   * Measured on the captured upper jaw: fusion leaves 643 open edges in 234
   * loops plus 628 same-direction pairs; the repair takes both to zero in
   * three rounds and the shell comes out closed, wall 88 % within 10 % of
   * target, escapes at the 0.05 mm measurement threshold.
   *
   * Before any of that, the interproximal space is rebuilt: the walls
   * segmentation drew for the scan positions are deleted and the arcs they
   * leave behind are lofted across to the neighbouring crown, so the space
   * closes where the teeth stand NOW rather than where they stood at the scan
   * (see interproximalRebuild). `options.joinInterdentalGum` decides whether
   * the gum's own bridges go with them.
   */
  public async exportFusedShellToSTL(
    thicknessMm = 1.5,
    skirtVia: 'mesh' | 'voxel' = 'mesh',
    options: FusedShellExportOptions = {},
  ): Promise<DataView | string> {
    const rebuildInterproximal = options.rebuildInterproximal ?? true;
    const joinInterdentalGum = options.joinInterdentalGum ?? true;
    const stage = (name: string) => console.log(`exportFusedShellToSTL (${this.jawType}): ${name}`);
    const opens = this.jawType === 'upper' ? ('up' as const) : ('down' as const);
    stage(`bake gum + teeth (skirt via ${skirtVia})`);
    this.updateWorldMatrix(true, false);
    // Bake with the PLY's own face order restored: `generatedFacesFrom` only
    // means anything in that order, and the live geometries' indices have
    // been BVH-sorted since load (computeBoundsTree reorders in place). The
    // position attribute is never reordered, so the PLY index is still valid
    // against the current (moved, deformed) positions.
    const restoreOrder = (baked: BufferGeometry, order: Uint32Array | null) => {
      if (order) baked.setIndex(new BufferAttribute(order.slice(), 1));
      return baked;
    };
    const gum = restoreOrder(
      this.geometry.clone().applyMatrix4(this.matrixWorld),
      this.gumPlyFaceOrder,
    );
    const crowns: BufferGeometry[] = [];
    for (const tooth of this.teeth) {
      tooth.updateWorldMatrix(true, false);
      crowns.push(
        restoreOrder(tooth.geometry.clone().applyMatrix4(tooth.matrixWorld), tooth.plyFaceOrder),
      );
    }
    const parts = [gum, ...crowns];

    if (rebuildInterproximal) {
      // Segmentation drew the interproximal walls for the SCAN positions; by
      // now the crowns have been moved through the treatment cycles and those
      // walls describe a mouth that no longer exists. Delete them and loft the
      // arcs they left behind straight across to the neighbour — the space
      // between the posed crowns, closed by construction.
      const { geometry: bridges, stats } = rebuildInterproximalSpace(
        crowns,
        this.teeth.map((tooth) => tooth.generatedFacesFrom),
      );
      const strippedTotal = stats.stripped.reduce((sum, n) => sum + n, 0);
      stage(
        `interproximal rebuild: ${strippedTotal} generated face(s) deleted from ` +
          `${this.teeth.length} crown(s), ${stats.arcs} arc(s) → ${stats.bridged} band(s) ` +
          `(mean gap ${stats.meanGapMm.toFixed(2)} mm) + ${stats.capped} cap(s), ` +
          `${stats.triangles} triangle(s)`,
      );
      stage(`arcs per crown: ${stats.arcsPerSource.join(' ')}`);
      if (stats.bands.length > 0) {
        stage(
          `bands (verts×verts contact/mean mm): ${stats.bands
            .map((b) => `${b.a}×${b.b} ${b.contactMm.toFixed(2)}/${b.gapMm.toFixed(2)}`)
            .join('  ')}`,
        );
      }
      if (stats.reorientedBands > 0) {
        stage(
          `${stats.reorientedBands} band(s) re-oriented — those arcs did not read ` +
            `antiparallel, so one side's winding is left to the manifold repair`,
        );
      }
      if (stats.nearestRejected.length > 0) {
        // A capped arc at each end of the arch is expected — there is nothing
        // on the far side to bridge to. Caps in the MIDDLE of the arch mean
        // the pairing failed, and the closest approach says why.
        stage(
          `capped (verts@closest partner): ${stats.nearestRejected
            .map((c) => `${c.verts}@${Number.isFinite(c.contactMm) ? c.contactMm.toFixed(2) : '∞'}`)
            .join(' ')}`,
        );
      }
      if (strippedTotal === 0) {
        stage(
          'WARNING: no crown carried a generated-face marker — this jaw was segmented ' +
            'before the marker existed, so its scan-position walls ship as they are',
        );
      }
      if (bridges) parts.push(bridges);

      if (!joinInterdentalGum) {
        const removed = stripGeneratedFaces(gum, this.gumGeneratedFacesFrom);
        stage(`interdental gum: ${removed} bridge face(s) deleted — embrasures left open`);
      }
    }

    const gumForSkirt = skirtVia === 'voxel' ? gum.clone() : null;

    stage('fuse seams');
    const { geometry: fused, stats: fusion } = fuseSeamTJunctions(parts);
    parts.forEach((p) => p.dispose());
    stage(`seam fusion ${JSON.stringify(fusion)}`);

    // Floating scan debris offsets into tiny closed shells hovering next to
    // the model — the slicer decorates each with its own support tower.
    const debris = removeSmallComponents(fused, 500);
    if (debris > 0) stage(`removed ${debris} debris component(s)`);

    // First repair BEFORE the skirt, so the rim walks run on a manifold sheet
    // and cannot derail into a defect slit near a seam. The real rims are
    // protected from the fills by the shared perimeter threshold.
    stage('repair to manifold');
    const repair = repairToManifold(fused, 0, 6, 60);
    stage(`manifold repair ${JSON.stringify(repair)}`);

    let skirtPlaneY = Number.NaN;
    if (skirtVia === 'mesh') {
      stage('extrude gum skirt');
      // A clean vertical skirt instead of flattening: two rows of
      // scanner-junk boundary faces are stripped, then new bands run from the
      // real rims down to one common plane — pure geometry, walls in one
      // direction, bottom rings planar by construction. Done AFTER fusion and
      // repair, when the tooth sockets are interior topology and the long
      // border loops are the real rims. The rings stay open on purpose: the
      // offset bridges them into the flat base the print stands on, so the
      // shell is an open-bottom cup and resin drains.
      const skirt = extrudeGumSkirt(fused, opens);
      skirtPlaneY = skirt.planeY;
      stage(
        `skirt: ${skirt.skirted} rim(s) of ${skirt.loops} border loop(s) taken to ` +
          `y=${skirt.planeY.toFixed(2)}, ${skirt.trimmedFaces} junk boundary faces stripped`,
      );
      if (skirt.skirted === 0) {
        stage('WARNING: no rim long enough to skirt — the shell will have no open bottom');
      }

      // Second repair: the strip may have exposed defects one row deeper, and
      // the census has to end clean or the shell will not close. The skirt's
      // bottom rings are long, so the same perimeter rule protects them.
      const settle = repairToManifold(fused, 0, 6, 60);
      stage(`post-skirt repair ${JSON.stringify(settle)}`);
      if (settle.openEdgesAfter > 0 || settle.sameDirectionAfter > 0) {
        stage(
          `WARNING: sheet still has ${settle.openEdgesAfter} open / ` +
            `${settle.sameDirectionAfter} conflicted edges — the shell may not close`,
        );
      }
    }

    if (options.outerOnly) {
      // Hand back the surface the hollowing step would have been given, so it
      // can be thickened anywhere else on identical input. The open edges below
      // are the base rim, which is open by design — not a defect count.
      gumForSkirt?.dispose();
      const positions = fused.getAttribute('position');
      stage(
        `outer sheet only: ${(fused.index?.count ?? positions.count) / 3} triangle(s), ` +
          `${positions.count} vertices, ${countOpenEdges(fused)} open edge(s) at the rim`,
      );
      stage('orient for print + write STL');
      return this.orientAndWriteSTL(fused);
    }

    const interior = options.interior ?? 'levelset';
    if (interior === 'voxel') {
      // Marching cubes, but only on the cavity — the outer sheet above goes
      // into the STL untouched. The cavity gets its OWN topology, which is the
      // one thing a per-vertex inner wall cannot have and the reason it jumps
      // wherever the body is thinner than two walls (see voxelInterior).
      if (!Number.isFinite(skirtPlaneY)) {
        throw new Error(
          'exportFusedShellToSTL: the voxel interior needs the base plane the mesh skirt ' +
            "establishes — run it with skirtVia 'mesh'",
        );
      }
      gumForSkirt?.dispose();
      stage('voxel interior');
      const built = await buildVoxelInteriorShell(fused, {
        thicknessMm,
        opens,
        planeY: skirtPlaneY,
      });
      if (!built) {
        fused.dispose();
        throw new Error(
          'exportFusedShellToSTL: the voxel interior found no cavity to build — the body is ' +
            'nowhere thicker than two walls, or its rim is not on the skirt plane',
        );
      }
      fused.dispose();
      const { stats } = built;
      stage(
        `voxel interior: cell ${stats.cellMm.toFixed(3)} mm, ` +
          `${(stats.cells / 1e6).toFixed(1)}M cells, ${stats.cavityTriangles} cavity triangle(s) ` +
          `taking out ${stats.cavityVolumeMm3.toFixed(0)} mm³, ${stats.bubblesRemoved} bubble(s) ` +
          `dropped, ${stats.cavityCracksFilled} crack(s) filled`,
      );
      stage(
        `rim cap: ${stats.rimLoops} loop(s) on the plane → ${stats.capTriangles} triangle(s) ` +
          `[${stats.loopReport.join(', ')}]`,
      );
      if (stats.strayLoops > 0) {
        stage(
          `WARNING: ${stats.strayLoops} border loop(s) are not on the skirt plane and were ` +
            'left uncapped — the outer shell will not close',
        );
      }
      if (stats.cavityOpenEdges > 0) {
        stage(`WARNING: the cavity kept ${stats.cavityOpenEdges} open edge(s) after repair`);
      }
      if (!stats.capFacesOutward) {
        stage('WARNING: the rim cap faces inward — the fused sheet is wound inside-out');
      }
      const openBase = countOpenEdges(built.geometry);
      if (openBase > 0) stage(`WARNING ${openBase} open edges — not a closed solid`);
      stage('orient for print + write STL');
      return this.orientAndWriteSTL(built.geometry);
    }

    // The INNER wall as an iso-surface of a distance field — the construction
    // every other hollowing tool uses. The outer surface is untouched either
    // way; only the inside changes, and no foil ever touches that.
    let inner: OffsetShellOptions = { thicknessMm };
    if (interior === 'levelset') {
      stage('inside distance field');
      const field = await buildInsideDistanceField(fused, { thicknessMm, opens });
      stage(
        `field: cell ${field.cellMm.toFixed(2)} mm, band ${field.bandMm.toFixed(2)} mm, ` +
          `${(field.cells / 1e6).toFixed(1)}M cells`,
      );
      inner = { thicknessMm, fit: 'levelset', field: field.field };
    }

    stage('offset shell');
    const shell = buildOffsetShell(fused, inner);
    fused.dispose();
    stage(
      `wall median ${shell.medianThicknessMm.toFixed(3)} mm, ${shell.taperedVertices} tapered, ` +
        `${shell.outsideVertices} outside by ≤${shell.outsideDepthMm.toFixed(3)} mm, ` +
        `${shell.jaggedVertices} jagged, ${shell.solidifiedVertices} solid, ` +
        `${shell.isoStalledVertices} too thin to hollow, ${shell.borderLoops} rim loop(s)`,
    );
    const open = countOpenEdges(shell.geometry);
    if (open > 0) stage(`WARNING ${open} open edges — not a closed solid`);

    let printable = shell.geometry;
    if (gumForSkirt) {
      stage('voxel skirt body');
      const body = await buildVoxelSkirtBody(gumForSkirt, opens, thicknessMm);
      gumForSkirt.dispose();
      if (body) {
        stage(
          `voxel skirt: ${body.skirted} rim(s), base y=${body.planeY.toFixed(2)}, ` +
            `${countOpenEdges(body.geometry)} open edges`,
        );
        printable = mergePositionIndexGeometries([shell.geometry, body.geometry]);
        shell.geometry.dispose();
        body.geometry.dispose();
      } else {
        stage('WARNING: voxel skirt found no rim to hang from — exporting the shell alone');
      }
    }

    stage('orient for print + write STL');
    return this.orientAndWriteSTL(printable);
  }

  /**
   * three.js is Y-up and slicers are Z-up: the base plane goes onto the build
   * plate, and the two jaws open opposite ways. Consumes `printable`.
   */
  private orientAndWriteSTL(printable: BufferGeometry): DataView | string {
    printable.rotateX(this.jawType === 'upper' ? -Math.PI / 2 : Math.PI / 2);
    printable.computeVertexNormals();
    const stl = MeshUtils.exportMeshToSTL(new Mesh(printable));
    printable.dispose();
    return stl;
  }

  /**
   * (Re)draws the gum wall carried down to the print's base plane, as a
   * VIEW-ONLY child.
   *
   * The viewer otherwise shows the gum ending at the scan's ragged edge while
   * the print stands on a flat base, so the two never look like the same
   * object. This draws the same bands `extrudeGumSkirt` will build at export
   * time — the same function, so what is on screen is what will be printed
   * rather than an impression of it.
   *
   * Rebuilt rather than transformed, and rebuilt on every gum write: moving a
   * tooth deforms the gum through ARAP, which moves the rim the skirt hangs
   * from. A cached skirt would drift away from the gum it belongs to over a
   * few cycles.
   *
   * It is a child mesh and nothing else: not pickable, not in the half-edge
   * structure, and never touched by any export, which builds its own skirt on
   * baked world geometry.
   */
  private refreshBasePreview(): void {
    if (this.basePreview) {
      this.remove(this.basePreview);
      this.basePreview.geometry.dispose();
      this.basePreview = null;
    }

    const opens = this.jawType === 'upper' ? ('up' as const) : ('down' as const);
    const draft = this.geometry.clone();
    const gumVertices = draft.getAttribute('position').count;
    const skirt = extrudeGumSkirt(draft, opens);
    if (skirt.skirted === 0 || !Number.isFinite(skirt.planeY)) {
      draft.dispose();
      // Once, not on every gum write — this fires on each tooth release.
      if (!this.basePreviewWarned) {
        this.basePreviewWarned = true;
        console.log(`refreshBasePreview (${this.jawType}): no rim long enough to skirt`);
      }
      return;
    }

    // Keep only what the skirt ADDED. It appends its bottom ring after the
    // gum's own vertices, so a face touching one of those is a band and
    // everything else is the gum, which is already on screen.
    const index = draft.getIndex()!;
    const bands: number[] = [];
    for (let i = 0; i < index.count; i += 3) {
      const a = index.getX(i);
      const b = index.getX(i + 1);
      const c = index.getX(i + 2);
      if (a >= gumVertices || b >= gumVertices || c >= gumVertices) bands.push(a, b, c);
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', (draft.getAttribute('position') as BufferAttribute).clone());
    geometry.setIndex(new BufferAttribute(new Uint32Array(bands), 1));
    geometry.computeVertexNormals();
    draft.dispose();

    const preview = new Mesh(geometry, gumMaterial);
    preview.name = 'gum-base-preview';
    // Not pickable: a tooth selection must never land on scenery.
    preview.raycast = () => {};
    this.basePreview = preview;
    this.add(preview);
  }

  /**
   * The gum rim, sealed/flattened on a THROWAWAY copy. Sealing is destructive
   * DS surgery — border collapses, a triangulated cap, a full rebuild — and
   * the DS can refuse it (both edge collapses and the cap's earcut triangles
   * reach addEdge, which throws where a fan has no free gap). Running it on
   * the LIVE gum had two ends, both bad: a failure left the app's gum
   * half-collapsed, and a success left it capped, so the NEXT export's
   * "largest border loop" was a tooth socket. The live gum is never touched
   * here. Plan B on refusal is pure flattening — keepFraction 1 and no band
   * work mean the only thing that happens to the copy is rim positions moving
   * onto the plane, no DS surgery at all, so nothing left to refuse.
   */
  private sealedGumCopy(opts: SealOuterLoopOptions): { sheet: BufferGeometry; planeY: number } {
    const flattenMode = this.jawType === 'upper' ? ('borderMax' as const) : ('borderMin' as const);
    const sealOnCopy = (extra: SealOuterLoopOptions) => {
      const copy = new HalfEdgeMesh(this.geometry.clone(), false);
      const seal = sealOuterBorderLoop(copy, { flattenMode, ...opts, ...extra });
      return { seal, sheet: copy.geometry };
    };
    let sealed: ReturnType<typeof sealOnCopy>;
    try {
      sealed = sealOnCopy({});
    } catch (error) {
      console.log(
        `sealedGumCopy (${this.jawType}): seal refused ` +
          `(${error instanceof Error ? error.message : String(error)}) — flatten-only fallback`,
      );
      sealed = sealOnCopy({ cap: false, keepFraction: 1, wallSmoothRings: 0 });
    }
    if (sealed.seal.planeY === undefined) {
      throw new Error('sealedGumCopy: rim plane could not be resolved');
    }
    return { sheet: sealed.sheet, planeY: sealed.seal.planeY };
  }

  /**
   * The gum alone as a hollow, open-bottom solid: the voxel route of
   * exportHollowModelToSTL, minus everything that only exists because teeth
   * are present — no seam fusion, no interproximal blockout, no channel
   * handling. The tooth sockets stay as free rims and the slab gives them a
   * flush-cut wall, so the body is watertight up to its open bottom and open
   * sockets, and the crowns' own shells cover those from above in the print.
   *
   * Voxels are the right tool HERE, unlike for the crowns: the gum is one
   * smooth sheet whose fidelity the appliance barely feels, so the round trip
   * costs nothing that matters — while the socket rims it shares with the
   * crowns would make a direct offset fight the same torn-border problem the
   * crowns' rim needed settleRim for, with none of the payoff.
   */
  private async buildGumShellGeometry(thicknessMm: number): Promise<BufferGeometry> {
    const stage = (name: string) => console.log(`buildGumShellGeometry (${this.jawType}): ${name}`);
    stage('seal/flatten + cap (throwaway copy)');
    const { sheet, planeY } = this.sealedGumCopy({});
    this.updateWorldMatrix(true, false);
    const gum = sheet.applyMatrix4(this.matrixWorld);

    // Same resolution policy as the fused export, without the blockout knob:
    // no interproximal gaps to keep open here, so the memory budget and the
    // 0.15 mm floor decide alone.
    gum.computeBoundingBox();
    const bb = gum.boundingBox!;
    const pad = 1.5;
    const volume =
      (bb.max.x - bb.min.x + 2 * pad) *
      (bb.max.y - bb.min.y + 2 * pad) *
      (bb.max.z - bb.min.z + 2 * pad);
    const cell = Math.min(0.3, Math.max(0.15, Math.cbrt(volume / 120_000_000)));

    // The flood rasterizer samples triangle interiors — the cap's huge earcut
    // triangles must be split below its reach or the flood leaks through.
    stage(`sdf (cell ${cell.toFixed(2)} mm)`);
    const soup = subdivideLongTriangles(gum, 1.5);
    const generator = new SDFGenerator([new Mesh(soup)]);
    const grid = await generator.generateNarrowBandFlood(
      {
        cellSize: cell,
        padding: pad,
        narrowBandMm: thicknessMm + 6 * cell,
        openSheet: true,
      },
      getSdfWorkerPool(),
    );
    generator.dispose();
    soup.dispose();
    closeSDFInPlace(grid, 1, '26-connected');
    applySheetSlab(grid, thicknessMm);

    stage('open bottom');
    const { origin, counts, strides, cellSize } = grid.info;
    const data = grid.data;
    const planeWorldY = this.localToWorld(new Vector3(0, planeY, 0)).y;
    const cutMargin = thicknessMm + 2 * cellSize;
    const cutY = this.jawType === 'upper' ? planeWorldY - cutMargin : planeWorldY + cutMargin;
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        const wy = origin.y + y * cellSize;
        const sd = this.jawType === 'upper' ? wy - cutY : cutY - wy;
        if (sd <= -cellSize) continue;
        let idx = x * strides.x + y * strides.y;
        for (let z = 0; z < counts.z; z++, idx++) {
          if (sd > data[idx]) data[idx] = sd;
        }
      }
    }

    stage('marching cubes');
    const raw = extractMesh(grid, { computeNormals: false });
    const welded = weldMeshVertices(raw, cellSize * 0.05);
    raw.dispose();
    for (let pass = 0; pass < 3; pass++) {
      if (fillMinorBoundaryLoops(welded, 0) === 0) break;
    }
    removeSmallComponents(welded, 500);

    // Smooth first, then a sharp-feature-masked fit back onto the gum sheet:
    // what little the gum has worth keeping crisp (the socket margins) comes
    // back, the scan's roughness stays smoothed away.
    stage('smooth + reproject');
    smoothSurface(welded, 12, 0.5, -0.53, thicknessMm / 6);
    const sourceBvh = new MeshBVH(gum);
    const sharpFaces = markSharpFaces(gum, 50);
    const band = Math.min(2 * cellSize, thicknessMm * 0.4);
    for (let pass = 0; pass < 2; pass++) {
      if (projectOntoSource(welded, sourceBvh, band, cellSize * 0.75, sharpFaces).moved === 0) break;
    }
    gum.dispose();

    // Marching cubes emits inward winding for our negative-inside field.
    const wound = welded.getIndex()!;
    for (let i = 0; i < wound.count; i += 3) {
      const tmp = wound.getX(i + 1);
      wound.setX(i + 1, wound.getX(i + 2));
      wound.setX(i + 2, tmp);
    }
    return welded;
  }

  public sealOuterGumLoop(opts?: SealOuterLoopOptions): SealOuterLoopResult {
    // The upper jaw's free rim points up, so its cap belongs on top.
    const flattenMode = opts?.flattenMode ?? (this.jawType === 'upper' ? 'borderMax' : 'borderMin');
    const result = sealOuterBorderLoop(this, { ...opts, flattenMode });

    // The DS was rebuilt → re-derive the seam ownership and rebind against the new vertices.
    this.gumBoundaryToTooth.clear();
    this.gumBoundaryToToothVertex.clear();
    this.alignedBoundaryVerticesByTooth.clear();
    this.gumVertexLocalInTooth.clear();
    this.mapGumBoundaryVerticesToTeeth();
    this.snapMappedGumVerticesToTeeth();
    this.bindGumVerticesToTeethLocal();
    this.captureInitialGumPositions();

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
    return result;
  }

  /**
   * Deforms the gum around a specific tooth.
   *
   * This method updates the positions of the gum vertices around the specified tooth
   * based on the tooth's current position and orientation.
   *
   * @param {ToothCorrection} tooth - The tooth around which the gum is to be deformed.
   */
  public deformGumAroundTooth(tooth: ToothCorrection) {
    const toothIndex = this.teeth.indexOf(tooth);
    if (toothIndex < 0) return;

    for (const [gv, ti] of this.gumBoundaryToTooth) {
      if (ti !== toothIndex) continue;
      const toothVertex = this.gumBoundaryToToothVertex.get(gv);
      if (!toothVertex) continue;
      const hitWorld = tooth.getVertexWorldPosition(toothVertex);
      const bestLocal = this.worldToLocal(hitWorld.clone());
      this.moveVertex(gv, bestLocal);
    }

    this.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Animates the jaw and teeth transformations for each iteration.
   *
   * This method animates the jaw and teeth transformations for each iteration defined in the treatmentCyclesTransformation object.
   * It updates the positions of the teeth and deforms the gum around each tooth based on the transformations defined for each iteration.
   */

  public async animateIterations() {
    this.teeth.forEach((tooth) => {
      tooth.detach();

      const initialPosition = tooth.getInitialPosition().clone();
      const initialQuaternion = tooth.getInitialQuaternion();

      tooth.setPosition(initialPosition);
      tooth.setRotationFromQuaternion(initialQuaternion);
      tooth.rotateHalfEdges(initialQuaternion, initialPosition);

      tooth.geometry.computeBoundingBox();
      tooth.geometry.computeBoundsTree();
    });

    // Ensure world matrices reflect the reset transforms before rebinding gum vertices
    this.rootState.scene.updateMatrixWorld(true);

    this.resetGumToInitialPositions();

    //TODO: check this..... Recompute seam ownership and align the gum directly on teeth to restore the original seal
    //this.mapGumBoundaryVerticesToTeeth();
    //this.snapMappedGumVerticesToTeeth();
    //this.bindGumVerticesToTeethLocal();
    //this.captureInitialGumPositions();

    // Guarantee seam vertices are perfectly aligned to their owning teeth after the reset
    this.teeth.forEach((tooth) => {
      this.updateGumVerticesAttachedToTooth(tooth);
    });

    await new Promise((resolve) => setTimeout(resolve, 1000));

    for (let i = 0; i < this.treatmentCyclesTransformation.getIterationsCount(); i++) {
      this.applyIterationTransformations(i);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    this.applyIterationTransformations(this.treatmentCyclesTransformation.getIterationsCount() - 1);
  }

  /*
  public downloadData() {
    this.applyIterationTransformations(this.treatmentCyclesTransformation.getIterationsCount() - 1);
    downloadJSONData(this.toJawExportDTO(), 'correction.json');
  }
  */

  /**
   * Converts the jaw and teeth mesh data to a format suitable for export.
   * @returns {treatmentCyclesDTO} - An array of objects containing the mesh data for each iteration.
   */
  public toTreatmentCyclesDTO(): treatmentCyclesDTO {
    const result: treatmentCyclesDTO = [];
    for (let i = 0; i < this.treatmentCyclesTransformation.getIterationsCount(); i++) {
      this.applyIterationTransformations(i);
      result.push({ week: i + 1, jaw: this.toJawExportDTO() });
    }
    return result;
  }

  public loadCorrections(toothCorrections: ToothCorrectionResponse[], gumBoundaryIndices?: number[]) {
    this.treatmentCyclesTransformation.resetIterations();
    toothCorrections.forEach((correction) => {
      const movement = new Vector3(...correction.movement);
      const rotation = new Quaternion(...correction.rotation);
      this.treatmentCyclesTransformation.calculateIterations(correction.fdi, movement, rotation);
    });
    
    if (gumBoundaryIndices && gumBoundaryIndices.length > 0) {
      this.setGumBoundaryIndices(gumBoundaryIndices);
    }
  }

  /**
   * Builds the payload used when persisting corrections.
   * Returns the jaw DTO (with tooth border vertices) together with the jaw mesh as a binary blob.
   */
  public async toCorrectionUploadBundle(_treatmentWeek?: number): Promise<{ data: JawCorrectionCreate; model: Blob }> {
    // Export gum PLY only — do not cut tooth meshes here; cutBelowGumLine replaces
    // geometry without centerOrigin() and permanently shifts teeth in the live scene.

    // Persist the pose currently on screen (applyCycleState should have set this).
    const teethCorrectionData = this.teeth.map((tooth) => tooth.toCorrectionDTO());

    const gumBoundaryIndices: number[] = [];
    this.gumBoundaryToTooth.forEach((_toothIndex, vertex) => {
      gumBoundaryIndices.push(vertex.renderId);
    });

    const model = await this.toPly({ binary: true });
    return {
      data: { 
        teethCorrectionData,
        gumBoundaryIndices
      },
      model,
    };
  }

  /**
   * Converts the jaw and teeth mesh data to a format suitable for export.
   * This method maps the vertices of each tooth to the corresponding vertices around the gum,
   * and creates a new connected mesh geometry that combines the teeth and gum into a single mesh.
   * The resulting mesh geometry is then converted to a DTO
   * that can be used to export the mesh data.
   * @returns {JawExportDTO} - A DTO containing the mesh data for the connected jaw and teeth.
   */
  private toJawExportDTO(): JawExportDTO {
    const teethCorrectionDTO: TeethExportDTO = [];

    this.teeth.forEach((tooth) => {
      const toothVerticesAroundGum = tooth.getVerticesAroundGum();
      const toothCorrectionDTO: ToothExportDTO = { borderVertices: [] };
      const toothVerticesToJaw = new Map<Vertex, Vertex>();

      tooth.halfEdges.vertices.forEach((vertex) => {
        if (!toothVerticesAroundGum.has(vertex)) {
          const newJawVertex = this.halfEdges.addVertex(vertex.position, this.halfEdges.vertices.length, vertex.color);
          toothVerticesToJaw.set(vertex, newJawVertex);
        }
      });

      tooth.halfEdges.faces.forEach((face) => {
        const faceHalfEdges = [face.halfedge, face.halfedge.next, face.halfedge.prev];
        const newVertices = [];
        for (let i = 0; i < 3; i++) {
          const toothHalfEdge = faceHalfEdges[i];
          if (toothVerticesAroundGum.has(toothHalfEdge.vertex)) {
            newVertices.push(toothVerticesAroundGum.get(toothHalfEdge.vertex)!);
          } else {
            newVertices.push(toothVerticesToJaw.get(toothHalfEdge.vertex)!);
          }
        }
        const newHalfEdges = [];
        newHalfEdges.push(this.halfEdges.addEdge(newVertices[0], newVertices[1]));
        newHalfEdges.push(this.halfEdges.addEdge(newVertices[1], newVertices[2]));
        newHalfEdges.push(this.halfEdges.addEdge(newVertices[2], newVertices[0]));
        this.halfEdges.addFace(newHalfEdges);
      });
      toothVerticesAroundGum.forEach((gumVertex) => {
        toothCorrectionDTO.borderVertices.push(gumVertex.renderId);
      });
      teethCorrectionDTO.push(toothCorrectionDTO);
    });
    return {
      mesh: bufferGeometryToMeshDTO(this.halfEdges.excludedBufferGeometry(new Set<Face>()).newGeometry),
      teeth: teethCorrectionDTO,
    };
  }

  /**
   * Applies the transformations for a specific iteration to the jaw and teeth.
   *
   * This method updates the positions of the teeth and deforms the gum around each tooth
   * based on the transformations defined for the given iteration index.
   *
   * @param {number} iterationIndex - The index of the iteration whose transformations are to be applied.
   */
  public applyIterationTransformations(iterationIndex: number) {
    const iterationsByTooth = this.treatmentCyclesTransformation.getIterations();

    for (const tooth of this.teeth) {
      const toothIterations = iterationsByTooth.get(tooth.getFDI());
      if (!toothIterations || iterationIndex >= toothIterations.length) {
        continue;
      }

      const iteration = toothIterations[iterationIndex];
      const targetPosition = tooth.getInitialPosition().clone().add(iteration.location.clone());
      tooth.setPosition(targetPosition);

      const targetRotation = tooth.getInitialQuaternion().multiply(iteration.rotation.clone());
      tooth.setRotationFromQuaternion(targetRotation);
      tooth.rotateHalfEdges(targetRotation, targetPosition);

      this.updateGumVerticesAttachedToTooth(tooth);
    }

    // One global ARAP solve for the settled multi-tooth state (replaces relying on
    // the per-tooth diffusion above when ARAP is enabled).
    this.solveGumDeformationArap();
  }

  /**
   * Places the crowns where a SAVED correction left them. Rigid only — the gum is
   * not touched, because a saved correction ships its own already-deformed gum
   * (see JawCorrectionOptions.gumAlreadyPosed); re-deriving the deformation from
   * the stored poses would be a second, different answer.
   *
   * Corrections whose FDI is not in this jaw are ignored rather than guessed at.
   */
  public applyToothCorrections(corrections: ToothCorrectionResponse[]): number {
    const byFdi = new Map(this.teeth.map((tooth) => [tooth.getFDI(), tooth]));
    let placed = 0;
    for (const correction of corrections) {
      const tooth = byFdi.get(correction.fdi);
      if (!tooth) continue;
      tooth.applySavedCorrection(correction.movement, correction.rotation);
      placed += 1;
    }
    this.rootState.scene.updateMatrixWorld(true);
    return placed;
  }

  public setJawsCorrection(jawsCorrection: JawsCorrection) {
    this.jawsCorrection = jawsCorrection;
  }

  public getJawsCorrection() {
    return this.jawsCorrection;
  }

  public getJawType(): 'upper' | 'lower' {
    return this.jawType;
  }

  public updateContactOverlay(active: ToothCorrection): void {
    this.contactOverlay.update(active);
  }

  public clearContactOverlay(): void {
    this.contactOverlay.clear();
  }

  public onActiveToothTransformEnd(active: ToothCorrection): void {
    // Final clean gum deformation once the drag is released (ARAP, off-main-thread);
    // the live preview during drag was the cheaper ring diffusion.
    this.solveGumDeformationArapAsync();
    this.contactOverlay.update(active);
    this.initialPoseGhosts.refresh();
    this.jawsCorrection?.getOcclusionContactMap().refreshIfEnabled();
  }

  public setInitialPoseGhostsVisible(visible: boolean): void {
    this.initialPoseGhosts.setVisible(visible);
  }

  /** Terminates the ARAP worker (call on teardown to avoid leaking a worker per jaw). */
  public disposeArap(): void {
    this.arapWorker?.dispose();
    this.arapWorker = null;
  }

  public getTransformIterations() {
    return this.treatmentCyclesTransformation;
  }

  public mapToFDI(index: number): number {
    const lookup = this.jawType === 'upper' ? upperFDI : lowerFDI;
    if (index < 0 || index >= lookup.length) {
      console.warn(`mapToFDI: index ${index} out of bounds for ${this.jawType} jaw`);
      return lookup[Math.max(0, Math.min(lookup.length - 1, index))];
    }
    return lookup[index];
  }
}
